import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatCurrency } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Trash2, Edit2, Package2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const materialCategories = [
  { en: 'Flour & Grains', ar: 'طحين وحبوب' },
  { en: 'Oils & Fats', ar: 'زيوت ودهون' },
  { en: 'Sugar & Sweeteners', ar: 'سكر ومحليات' },
  { en: 'Dairy', ar: 'ألبان' },
  { en: 'Eggs', ar: 'بيض' },
  { en: 'Nuts & Dried Fruits', ar: 'مكسرات وفواكه مجففة' },
  { en: 'Chocolate & Cocoa', ar: 'شوكولاتة وكاكاو' },
  { en: 'Flavoring', ar: 'نكهات' },
  { en: 'Packaging', ar: 'تغليف' },
  { en: 'Cleaning', ar: 'تنظيف' },
  { en: 'Other', ar: 'أخرى' },
];

const units = ['kg', 'g', 'L', 'ml', 'piece', 'box', 'bag', 'pack'];

export default function MaterialsPage() {
  const { language } = useLanguage();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    category: 'Other',
    unit: 'kg',
    default_price: '',
    description: '',
  });

  const t = (key) => {
    const translations = {
      materials: { en: 'Raw Materials', ar: 'المواد الخام' },
      addMaterial: { en: 'Add Material', ar: 'إضافة مادة' },
      editMaterial: { en: 'Edit Material', ar: 'تعديل المادة' },
      name: { en: 'Name', ar: 'الاسم' },
      nameAr: { en: 'Arabic Name', ar: 'الاسم بالعربي' },
      category: { en: 'Category', ar: 'الفئة' },
      unit: { en: 'Unit', ar: 'الوحدة' },
      defaultPrice: { en: 'Default Price', ar: 'السعر الافتراضي' },
      description: { en: 'Description', ar: 'الوصف' },
      search: { en: 'Search materials...', ar: 'البحث في المواد...' },
      save: { en: 'Save', ar: 'حفظ' },
      cancel: { en: 'Cancel', ar: 'إلغاء' },
      confirm: { en: 'Are you sure?', ar: 'هل أنت متأكد؟' },
      noData: { en: 'No materials found', ar: 'لا توجد مواد' },
      totalMaterials: { en: 'Total Materials', ar: 'إجمالي المواد' },
    };
    return translations[key]?.[language] || translations[key]?.en || key;
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        default_price: parseFloat(formData.default_price) || 0,
      };

      if (editingMaterial) {
        await axios.put(`${API}/materials/${editingMaterial.id}`, data);
      } else {
        await axios.post(`${API}/materials`, data);
      }
      fetchMaterials();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving material:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm'))) {
      try {
        await axios.delete(`${API}/materials/${id}`);
        fetchMaterials();
      } catch (error) {
        console.error('Error deleting material:', error);
      }
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      name_ar: material.name_ar || '',
      category: material.category,
      unit: material.unit,
      default_price: material.default_price?.toString() || '',
      description: material.description || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMaterial(null);
    setFormData({
      name: '',
      name_ar: '',
      category: 'Other',
      unit: 'kg',
      default_price: '',
      description: '',
    });
  };

  const filteredMaterials = materials.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.name_ar?.toLowerCase().includes(search.toLowerCase()) ||
      m.category.toLowerCase().includes(search.toLowerCase())
  );

  const getCategoryLabel = (cat) => {
    const found = materialCategories.find(c => c.en === cat);
    return found ? (language === 'ar' ? found.ar : found.en) : cat;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="materials-title">{t('materials')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'Manage raw materials' : 'Manage raw materials'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-material-btn">
          <Plus size={18} className="me-2" />
          {t('addMaterial')}
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="border-border/40 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('totalMaterials')}</p>
              <p className="text-3xl font-bold text-amber-600" data-testid="total-materials">
                {materials.length}
              </p>
            </div>
            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
              <Package2 className="text-amber-600" size={28} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="materials-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filteredMaterials.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Package2 size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('name')}</TableHead>
                    <TableHead>{t('category')}</TableHead>
                    <TableHead>{t('unit')}</TableHead>
                    <TableHead>{t('defaultPrice')}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMaterials.map((material) => (
                    <TableRow key={material.id} data-testid={`material-row-${material.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {language === 'ar' && material.name_ar ? material.name_ar : material.name}
                          </p>
                          {language === 'ar' && material.name_ar && (
                            <p className="text-xs text-muted-foreground">{material.name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{getCategoryLabel(material.category)}</Badge>
                      </TableCell>
                      <TableCell>{material.unit}</TableCell>
                      <TableCell>{formatCurrency(material.default_price)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(material)}
                            data-testid={`edit-material-${material.id}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(material.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-material-${material.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMaterial ? t('editMaterial') : t('addMaterial')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('name')} (English)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Flour"
                  data-testid="material-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('nameAr')}</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  placeholder="مثال: طحين"
                  data-testid="material-name-ar-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('category')}</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="material-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {materialCategories.map((cat) => (
                      <SelectItem key={cat.en} value={cat.en}>
                        {language === 'ar' ? cat.ar : cat.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('unit')}</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger data-testid="material-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('defaultPrice')} (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.default_price}
                onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                placeholder="0.00"
                data-testid="material-price-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="material-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="material-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
